import React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, ChevronRight as ArrowR, Briefcase, CreditCard, Shield, Wrench } from "lucide-react";
import { useModals } from "@/lib/modal-context";

const SERVICES = [
    "Office fit-out",
    "Office renovation",
    "Home interiors",
    "Retail branding",
    "Restaurant interiors",
    "Signage & wall branding",
    "Glass frosting & partitions",
    "Furniture sourcing",
    "Lighting & flooring",
    "MEP coordination",
    "Civil work",
    "Final styling",
];

export default function WhatWeOfferMain() {
    const { openConnect } = useModals();
    return (
        <section id="about" className="bg-white py-16 sm:py-20">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="rounded-[2rem] border border-black/5 bg-white p-6 sm:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start fade-up">
                        <div className="lg:col-span-5">
                            <div className="text-[#7e9a00] text-xs font-bold tracking-[0.22em]">WHAT WE OFFER</div>
                            <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-[52px] font-extrabold leading-[1.05] tracking-[-0.03em]">
                                Built with trust.
                                <br />
                                <span className="text-[#7e9a00]">Designed for Dubai spaces.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-4 text-sm text-black/60 max-w-md mt-2">
                            From design to execution, we manage your space with clear pricing, premium finishes and a structured delivery plan.
                        </p>
                        <div className="lg:col-span-3 flex lg:justify-end">
                            <button
                                onClick={() => openConnect({ source: "free_quote_main" })}
                                data-testid="get-free-quote-btn-main"
                                className="btn-pill btn-lime pr-1.5"
                            >
                                <span className="px-1 text-sm font-bold">GET FREE QUOTE</span>
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                    <ArrowRight className="h-4 w-4" />
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="relative mt-12">
                        <button className="hidden lg:flex absolute left-[-22px] top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white border border-black/10 shadow-md hover:bg-[#CCFF00]" aria-label="prev">
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button className="hidden lg:flex absolute right-[-22px] top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center rounded-full bg-white border border-black/10 shadow-md hover:bg-[#CCFF00]" aria-label="next">
                            <ChevronRight className="h-5 w-5" />
                        </button>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            {/* Services */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <Briefcase className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Our services</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">End-to-end solutions across offices, homes, retail and restaurants.</p>
                                    </div>
                                </div>
                                <ul className="mt-6 divide-y divide-black/10">
                                    {SERVICES.map((s) => (
                                        <li key={s} className="flex items-center justify-between py-3 group cursor-pointer">
                                            <span className="flex items-center gap-3 text-sm font-semibold">
                                                <span className="h-2 w-2 rounded-full bg-[#7e9a00]" />
                                                {s}
                                            </span>
                                            <ArrowR className="h-4 w-4 text-black/40 group-hover:text-black group-hover:translate-x-1 transition" />
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Price benefits */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <CreditCard className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Price benefits</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">Honest pricing, flexible options and complete transparency.</p>
                                    </div>
                                </div>
                                <div className="mt-6 space-y-5">
                                    {[
                                        { title: "Transparent quotations", desc: "Clear, itemised pricing you can review line by line before we begin.", bg: "#FFE3E3" },
                                        { title: "Flexible package options", desc: "Choose a package tier that matches your scope, ambition and budget.", bg: "#F7F4DA" },
                                        { title: "No surprise additions", desc: "Any change is approved by you first — never billed without confirmation.", bg: "#FFD6BC" },
                                        { title: "Budget-based material options", desc: "Multiple material tiers so quality scales with your budget.", bg: "#E5DEFF" },
                                    ].map((p) => (
                                        <div key={p.title} className="flex gap-3">
                                            <div className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: p.bg }}>
                                                <CreditCard className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <div className="font-display text-[15px] font-bold">{p.title}</div>
                                                <p className="mt-0.5 text-[13px] text-black/60 leading-snug">{p.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Warranty / Support */}
                            <div className="rounded-3xl bg-white border border-black/10 p-6 sm:p-8 fade-up">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F7F4DA]">
                                        <Shield className="h-6 w-6 text-[#7e9a00]" />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-xl font-bold">Warranty &amp; support</h3>
                                        <p className="mt-1 text-sm text-black/60 max-w-[16rem]">Long-term assurance and post-handover peace of mind.</p>
                                    </div>
                                </div>
                                <div className="mt-6 space-y-5">
                                    {[
                                        { title: "Quality-focused execution", desc: "Site supervision and finishing checks at every stage.", icon: <Shield className="h-4 w-4" />, bg: "#E5DEFF" },
                                        { title: "Vendor-backed material warranties", desc: "Manufacturer warranties on eligible materials, passed on to you.", icon: <Shield className="h-4 w-4" />, bg: "#F7F4DA" },
                                        { title: "Post-handover support", desc: "We stay reachable after handover for touch-ups and coordination.", icon: <Wrench className="h-4 w-4" />, bg: "#FFE3E3" },
                                        { title: "On-site coordination", desc: "Single point of contact managing all trades and vendors.", icon: <Wrench className="h-4 w-4" />, bg: "#E5DEFF" },
                                    ].map((p) => (
                                        <div key={p.title} className="flex gap-3">
                                            <div className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: p.bg }}>
                                                {p.icon}
                                            </div>
                                            <div>
                                                <div className="font-display text-[15px] font-bold">{p.title}</div>
                                                <p className="mt-0.5 text-[13px] text-black/60 leading-snug">{p.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
